// First import in main.tsx (§28.1). No-ops cleanly with no DSN set.
import * as Sentry from '@sentry/react';

const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const VIN_RE = /\b[A-HJ-NPR-Z0-9]{17}\b/g;
const TOKEN_RE = /\b(Bearer|bearer)\s+[A-Za-z0-9._-]+/g;
const SECRET_RE = /client_secret["':\s]+[A-Za-z0-9_]+/g;
const SHARE_URL_RE = /(\/q\/)([A-Za-z0-9_-]{6})[A-Za-z0-9_-]+/g;

function scrub(input: string): string {
  return input
    .replace(EMAIL_RE, '[redacted-email]')
    .replace(VIN_RE, '[redacted-vin]')
    .replace(TOKEN_RE, '$1 [redacted-token]')
    .replace(SECRET_RE, 'client_secret: [redacted]')
    .replace(SHARE_URL_RE, '$1$2…');
}

function deepScrub<T>(value: T): T {
  if (typeof value === 'string') return scrub(value) as unknown as T;
  if (Array.isArray(value)) return value.map((v) => deepScrub(v)) as unknown as T;
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (k.toLowerCase() === 'authorization' || k.toLowerCase() === 'cookie') continue;
      out[k] = deepScrub(v);
    }
    return out as T;
  }
  return value;
}

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  // Only report from real deployments - never from a local dev machine, even
  // if a DSN leaks into a developer's .env. Dev traffic pollutes the prod
  // project and floods every page load with session envelopes.
  if (!dsn || !import.meta.env.PROD) return;
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: `wrapforce@${import.meta.env.VITE_GIT_SHA ?? 'dev'}`,
    sendDefaultPii: false,
    tracesSampleRate: 0.1,
    // No session replay (CRM screens are full of customer PII, §28.1) and no
    // release-health session tracking. An `integrations: []` array does NOT
    // disable defaults in v8+ (it merges), so drop BrowserSession explicitly -
    // otherwise it emits a session envelope on every page load.
    integrations: (defaults) => defaults.filter((i) => i.name !== 'BrowserSession'),
    beforeSend(event) {
      if (event.request) delete event.request.data;
      return deepScrub(event);
    },
  });
}

export { Sentry };

export function crumb(category: string, message: string, data?: Record<string, unknown>): void {
  Sentry.addBreadcrumb({ category, message, data: data ? deepScrub(data) : undefined, level: 'info' });
}
